/**
 * Follow hooks — TanStack Query for follow/unfollow with optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchFollowStatus,
  followUser,
  unfollowUser,
  fetchFollowers,
  fetchFollowing,
} from '@turnifly/shared/api'
import type { FollowStatusResult, FollowListItem } from '@turnifly/shared/api'
import { profileKeys } from './use-profile'

// ── Query Keys ──────────────────────────────────────────────

export const followKeys = {
  status: (myId: string, targetId: string) =>
    ['follow', 'status', myId, targetId] as const,
  followers: (userId: string) => ['follow', 'followers', userId] as const,
  following: (userId: string) => ['follow', 'following', userId] as const,
} as const

// ── Follow Status ───────────────────────────────────────────

export function useFollowStatus(
  myId: string | undefined,
  targetId: string | undefined,
): ReturnType<typeof useQuery<FollowStatusResult>> {
  return useQuery({
    queryKey: followKeys.status(myId ?? '', targetId ?? ''),
    queryFn: () => fetchFollowStatus(myId!, targetId!),
    enabled: !!myId && !!targetId && myId !== targetId,
    staleTime: 30_000,
  })
}

// ── Follow / Unfollow ───────────────────────────────────────

interface FollowParams {
  followerId: string
  followingId: string
  isTargetPrivate: boolean
}

export function useToggleFollow(): ReturnType<typeof useMutation<void, Error, FollowParams & { isCurrentlyFollowing: boolean }>> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: FollowParams & { isCurrentlyFollowing: boolean }) => {
      if (params.isCurrentlyFollowing) {
        await unfollowUser(params.followerId, params.followingId)
      } else {
        await followUser(params.followerId, params.followingId, params.isTargetPrivate)
      }
    },
    onMutate: async (params) => {
      const statusKey = followKeys.status(params.followerId, params.followingId)
      await queryClient.cancelQueries({ queryKey: statusKey })
      const previousStatus = queryClient.getQueryData<FollowStatusResult>(statusKey)

      // Optimistic update
      const defaults = { isFollowedBy: false, isBlockedByTarget: false }
      if (params.isCurrentlyFollowing) {
        queryClient.setQueryData<FollowStatusResult>(statusKey, (old) => ({
          ...(old ?? defaults),
          isFollowing: false,
          followStatus: 'none',
        }))
      } else {
        queryClient.setQueryData<FollowStatusResult>(statusKey, (old) => ({
          ...(old ?? defaults),
          isFollowing: !params.isTargetPrivate,
          followStatus: params.isTargetPrivate ? 'pending' : 'accepted',
        }))
      }

      return { previousStatus, statusKey }
    },
    onError: (_err, _params, context) => {
      if (context?.previousStatus && context?.statusKey) {
        queryClient.setQueryData(context.statusKey, context.previousStatus)
      }
    },
    onSettled: (_data, _error, params) => {
      // Invalidate all related queries
      void queryClient.invalidateQueries({
        queryKey: followKeys.status(params.followerId, params.followingId),
      })
      void queryClient.invalidateQueries({
        queryKey: profileKeys.detail(params.followingId),
      })
      void queryClient.invalidateQueries({
        queryKey: profileKeys.detail(params.followerId),
      })
      void queryClient.invalidateQueries({
        queryKey: followKeys.followers(params.followingId),
      })
      void queryClient.invalidateQueries({
        queryKey: followKeys.following(params.followerId),
      })
      // Refresh feed's following IDs set (for follow buttons in feed)
      void queryClient.invalidateQueries({
        queryKey: ['feed', 'following-ids'],
      })
    },
  })
}

// ── Followers List ──────────────────────────────────────────

export function useFollowers(
  userId: string | undefined,
): ReturnType<typeof useQuery<FollowListItem[]>> {
  return useQuery({
    queryKey: followKeys.followers(userId ?? ''),
    queryFn: () => fetchFollowers({ userId: userId! }),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

// ── Following List ──────────────────────────────────────────

export function useFollowing(
  userId: string | undefined,
): ReturnType<typeof useQuery<FollowListItem[]>> {
  return useQuery({
    queryKey: followKeys.following(userId ?? ''),
    queryFn: () => fetchFollowing({ userId: userId! }),
    enabled: !!userId,
    staleTime: 60_000,
  })
}
